import type { VFC } from 'react';
import { type FormatNumberOptions, FormattedNumber as IntlFormattedNumber, type IntlShape } from 'react-intl';
import { isNil } from 'ramda';
import { useCustomerCurrencyReadable } from '@/containers/App';

interface FormatNumberConfig {
  value: string | number | null | undefined;
  maxFraction?: number;
  minFraction?: number;
  short?: boolean;
}

const DEFAULT_CONFIG = {
  minFraction: 0,
  maxFraction: 2,
} as const;

export const parseNumber = (value: string | number): number =>
  typeof value === 'string' ? parseFloat(value.replace(/,/g, '.').replace(/ /g, '')) : value;

interface NumberPartsShape extends Pick<FormatNumberOptions, 'minimumFractionDigits' | 'maximumFractionDigits'> {
  value: number | undefined;
  suffix: string;
}

const getNumberParts = (props: FormatNumberConfig): NumberPartsShape => {
  const { value, minFraction, maxFraction, short } = props;

  if (value === null || value === undefined) {
    return { value: undefined, suffix: '', minimumFractionDigits: 0, maximumFractionDigits: 0 };
  }

  const parsedValue = parseNumber(value);
  let suffix = '';
  let displayValue = parsedValue;

  if (Number.isNaN(parsedValue)) {
    return { value: undefined, suffix: '', minimumFractionDigits: 0, maximumFractionDigits: 0 };
  }

  if (short) {
    if (parsedValue >= 1000000) {
      suffix = 'mil';
      displayValue = parsedValue / 1000000;
    }
  }

  let fraction = 0;
  if (displayValue > -1 && displayValue < 1) {
    fraction = 4;
  } else if (displayValue > -100 && displayValue < 100) {
    fraction = 2;
  }

  return {
    value: displayValue,
    suffix,
    minimumFractionDigits: minFraction ?? DEFAULT_CONFIG.minFraction,
    maximumFractionDigits: Math.max(
      minFraction ?? DEFAULT_CONFIG.minFraction,
      Math.min(fraction, maxFraction ?? DEFAULT_CONFIG.maxFraction),
    ),
  };
};

/**
 * Returns formatted number as string
 */
export const formatNumber = (intl: IntlShape, props: FormatNumberConfig): string => {
  const { value, suffix, minimumFractionDigits, maximumFractionDigits } = getNumberParts(props);

  if (value === undefined) return '-';

  return `${intl.formatNumber(value, {
    minimumFractionDigits,
    maximumFractionDigits,
  })} ${suffix}`;
};

interface FormattedNumberProps extends FormatNumberConfig {
  /**
   * @deprecated Use `formatNumber` instead.
   */
  intl?: IntlShape;
}

// TODO: https://enmontech.atlassian.net/browse/ENMON-1649
// unify usage accross app, remove possible string return, intl from props, @ts-expect-error
const FormattedNumber: VFC<FormattedNumberProps> = props => {
  const { intl, ...restProps } = props;

  if (intl) {
    return formatNumber(intl, restProps);
  }

  const { value, suffix, minimumFractionDigits, maximumFractionDigits } = getNumberParts(restProps);

  if (value === undefined) {
    return <>-</>;
  }

  return (
    <>
      <IntlFormattedNumber
        maximumFractionDigits={maximumFractionDigits}
        minimumFractionDigits={minimumFractionDigits}
        value={value}
      />
      {suffix}
    </>
  );
};

interface FormattedPriceProps {
  value: string | number | null | undefined;
  withCurrency?: boolean;
}

export const FormattedPrice: VFC<FormattedPriceProps> = ({ value, withCurrency = false }) => {
  const currencyReadable = useCustomerCurrencyReadable();

  if (isNil(value)) {
    return <>-</>;
  }

  const parsedValue = parseNumber(value);

  if (Number.isNaN(parsedValue)) {
    return <>-</>;
  }

  return (
    <>
      <IntlFormattedNumber maximumFractionDigits={2} minimumFractionDigits={2} value={parsedValue} />
      {withCurrency && ` ${currencyReadable}`}
    </>
  );
};

export default FormattedNumber;
